import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, FilePlus, ImagePlus, MessageCircle, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, inr } from "@/lib/api";
import { SUBS_BY_SECTION } from "@/lib/categories";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const emptyForm = { name: "", price: "", category: "fashion", subcategory: "Coord Sets", stock: "10", description: "", images: [], sizesText: "", featured: false, is_new: false };
const emptyInvForm = { name: "", phone: "", address: "", tax: "0", notes: "" };
const emptyInvItem = { product_id: "", name: "", price: "", qty: "1" };
const emptyOrderForm = { name: "", phone: "", notes: "" };
const ORDER_LOG_STATUSES = ["new", "confirmed", "shipped", "delivered", "cancelled"];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const fileInputRef = useRef(null);
  const [invDialogOpen, setInvDialogOpen] = useState(false);
  const [invForm, setInvForm] = useState(emptyInvForm);
  const [invItems, setInvItems] = useState([{ ...emptyInvItem }]);
  const [invSaving, setInvSaving] = useState(false);
  const [aboutContent, setAboutContent] = useState(null);
  const [contentSaving, setContentSaving] = useState("");
  const [orderLogs, setOrderLogs] = useState([]);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderForm, setOrderForm] = useState(emptyOrderForm);
  const [orderItems, setOrderItems] = useState([{ ...emptyInvItem }]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [tileCovers, setTileCovers] = useState({});
  const [tileSaving, setTileSaving] = useState(false);

  const isAdmin = user && user.role === "admin";

  const load = useCallback(async () => {
    const [p, inv, rev, ord] = await Promise.all([api.get("/products", { params: { all_sections: true } }), api.get("/invoices"), api.get("/reviews"), api.get("/order-logs")]);
    setProducts(p.data);
    setInvoices(inv.data);
    setReviews(rev.data);
    setOrderLogs(ord.data);
  }, []);

  useEffect(() => {
    if (isAdmin) load().catch(() => {});
  }, [isAdmin, load]);

  useEffect(() => {
    if (isAdmin) api.get("/content/about").then((r) => setAboutContent(r.data)).catch(() => {});
    if (isAdmin) api.get("/tile-covers").then((r) => setTileCovers(r.data || {})).catch(() => {});
  }, [isAdmin]);

  if (user === null) return <div className="px-6 py-32 text-soot">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="px-6 py-32" data-testid="admin-denied">
        <p className="font-serif text-3xl italic text-soot">The admin panel is for the seller only.</p>
        <Link to="/auth" className="mt-4 inline-block text-xs font-bold uppercase tracking-[0.18em] underline underline-offset-8">
          Seller Sign In
        </Link>
      </div>
    );
  }

  const fashionCount = products.filter((p) => p.category === "fashion").length;
  const furnishingCount = products.filter((p) => p.category === "furnishing").length;

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setImageUrl("");
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      price: String(p.price),
      category: p.category,
      subcategory: p.subcategory || SUBS_BY_SECTION[p.category][0],
      stock: String(p.stock),
      description: p.description,
      images: p.images || [],
      sizesText: (p.sizes || []).join(","),
      featured: p.featured,
      is_new: p.is_new || false,
    });
    setImageUrl("");
    setDialogOpen(true);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5 MB or smaller");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addImageUrl = () => {
    const url = imageUrl.trim();
    if (!url) return;
    setForm((f) => ({ ...f, images: [...f.images, url] }));
    setImageUrl("");
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.subcategory) {
      toast.error("Please choose a category");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      price: parseFloat(form.price),
      category: form.category,
      subcategory: form.subcategory,
      stock: parseInt(form.stock || "0", 10),
      description: form.description,
      images: form.images,
      sizes: form.sizesText.split(",").map((s) => s.trim()).filter(Boolean),
      featured: form.featured,
      is_new: form.is_new,
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeReview = async (r) => {
    if (!window.confirm(`Delete review by "${r.name}"?`)) return;
    try {
      await api.delete(`/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ---------- order log ----------

  const openOrderDialog = () => {
    setOrderForm(emptyOrderForm);
    setOrderItems([{ ...emptyInvItem }]);
    setOrderDialogOpen(true);
  };

  const updateOrderItem = (idx, field, value) =>
    setOrderItems((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const pickOrderProduct = (idx, pid) => {
    if (pid === "custom") {
      setOrderItems((rows) => rows.map((r, i) => (i === idx ? { ...r, product_id: "custom", name: "", price: "" } : r)));
      return;
    }
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setOrderItems((rows) => rows.map((r, i) => (i === idx ? { ...r, product_id: pid, name: p.name, price: String(p.price) } : r)));
  };

  const orderTotal = orderItems.reduce((s, r) => s + (parseFloat(r.price) || 0) * (parseInt(r.qty, 10) || 0), 0);

  const saveOrder = async (e) => {
    e.preventDefault();
    const items = orderItems
      .filter((r) => r.name.trim())
      .map((r) => ({ name: r.name.trim(), price: parseFloat(r.price) || 0, qty: parseInt(r.qty, 10) || 1 }));
    if (items.length === 0) {
      toast.error("Add at least one item");
      return;
    }
    setOrderSaving(true);
    try {
      await api.post("/order-logs", { customer_name: orderForm.name, phone: orderForm.phone, items, notes: orderForm.notes });
      toast.success("Order logged");
      setOrderDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setOrderSaving(false);
    }
  };

  const setOrderStatus = async (o, status) => {
    try {
      await api.put(`/order-logs/${o.id}/status`, { status });
      toast.success("Order status updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Delete order for "${o.customer_name}"?`)) return;
    try {
      await api.delete(`/order-logs/${o.id}`);
      toast.success("Order deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ---------- subcategory tile covers ----------
  const saveTileCovers = async () => {
    setTileSaving(true);
    try {
      await api.put("/tile-covers", { covers: tileCovers });
      toast.success("Tile covers saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not save tile covers");
    } finally {
      setTileSaving(false);
    }
  };

  // ---------- about page content ----------

  const saveContent = async (key) => {
    setContentSaving(key);
    try {
      const { title, text, image } = aboutContent[key];
      const { data } = await api.put(`/content/${key}`, { title, text, image });
      setAboutContent((c) => ({ ...c, [key]: data }));
      toast.success("Page content updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setContentSaving("");
    }
  };

  const uploadContentImage = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5 MB or smaller");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd);
      setAboutContent((c) => ({ ...c, [key]: { ...c[key], image: data.url } }));
      toast.success("Image uploaded — press Save to publish");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      e.target.value = "";
    }
  };

  // ---------- invoices ----------

  const openInvoiceDialog = (order = null) => {
    if (order) {
      setInvForm({ name: order.customer_name, phone: order.phone || "", address: "", tax: "0", notes: order.notes || "" });
      setInvItems(order.items.map((i) => ({ product_id: "", name: i.name, price: String(i.price), qty: String(i.qty) })));
    } else {
      setInvForm(emptyInvForm);
      setInvItems([{ ...emptyInvItem }]);
    }
    setInvDialogOpen(true);
  };

  const updateInvItem = (idx, field, value) =>
    setInvItems((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const pickProduct = (idx, pid) => {
    if (pid === "custom") {
      setInvItems((rows) => rows.map((r, i) => (i === idx ? { ...r, product_id: "custom", name: "", price: "" } : r)));
      return;
    }
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setInvItems((rows) => rows.map((r, i) => (i === idx ? { ...r, product_id: pid, name: p.name, price: String(p.price) } : r)));
  };

  const invSubtotal = invItems.reduce((s, r) => s + (parseFloat(r.price) || 0) * (parseInt(r.qty, 10) || 0), 0);
  const invTax = Math.round((invSubtotal * (parseFloat(invForm.tax) || 0)) / 100 * 100) / 100;

  const saveInvoice = async (e) => {
    e.preventDefault();
    const items = invItems
      .filter((r) => r.name.trim())
      .map((r) => ({ name: r.name.trim(), price: parseFloat(r.price) || 0, qty: parseInt(r.qty, 10) || 1 }));
    if (items.length === 0) {
      toast.error("Add at least one item");
      return;
    }
    setInvSaving(true);
    try {
      const { data: inv } = await api.post("/invoices", {
        customer: { name: invForm.name, phone: invForm.phone, address: invForm.address },
        items,
        tax_percent: parseFloat(invForm.tax) || 0,
        notes: invForm.notes,
      });
      toast.success(`Invoice ${inv.invoice_no} created`);
      setInvDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInvSaving(false);
    }
  };

  const downloadPdf = (inv) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`, "_blank");
  };

  const shareOnWhatsApp = (inv) => {
    const digits = (inv.customer.phone || "").replace(/\D/g, "");
    const number = digits.length === 10 ? `91${digits}` : digits;
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/shared/${inv.share_token}/pdf`;
    const msg = [
      `Hi ${inv.customer.name}, thank you for shopping with Hunar Designs!`,
      "",
      `Your invoice ${inv.invoice_no} for ₹${Number(inv.total).toLocaleString("en-IN")} is ready.`,
      `Download it here: ${pdfUrl}`,
      "",
      "— Team Hunar Designs",
    ].join("\n");
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const removeInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_no}?`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="px-6 py-12 md:px-12 lg:px-20">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-soot">Hunar Designs</p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-serif text-4xl font-light tracking-tight sm:text-5xl">Seller Panel</h1>
          <div className="flex gap-2" data-testid="admin-tabs">
            {["overview", "products", "orders", "invoices", "about", "reviews"].map((t) => (
              <button
                key={t}
                data-testid={`admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`border px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                  tab === t ? "border-obsidian bg-obsidian text-bone" : "border-seam text-soot hover:border-obsidian"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" && (
          <div className="mt-12 grid gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="admin-overview">
            {[
              { label: "Total Products", value: products.length, id: "products" },
              { label: "Fashion Wear", value: fashionCount, id: "fashion" },
              { label: "Home Furnishing", value: furnishingCount, id: "furnishing" },
              { label: "Orders Logged", value: orderLogs.length, id: "orders" },
              { label: "Invoices", value: invoices.length, id: "invoices" },
            ].map((s) => (
              <div key={s.id} className="border border-seam p-8" data-testid={`stat-${s.id}`}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-soot">{s.label}</p>
                <p className="mt-3 font-serif text-5xl font-light">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <div className="mt-12" data-testid="admin-products">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">Products</h2>
              <Button data-testid="add-product-button" onClick={openCreate} className="rounded-full bg-obsidian text-bone hover:bg-olive">
                <Plus className="h-4 w-4" /> Add Product
              </Button>
            </div>
            <div className="mt-6 overflow-x-auto border border-seam">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Piece</TableHead>
                    <TableHead>Section</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Stock</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-9 shrink-0 overflow-hidden bg-linen">
                            {p.images?.[0] && <img src={p.images[0]} alt="" className="h-full w-full object-cover" />}
                          </div>
                          <span className="font-serif text-base">{p.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs uppercase tracking-wider">{p.category === "fashion" ? "Fashion" : p.category === "mumkey" ? "Mumkey" : "Furnishing"}</TableCell>
                      <TableCell className="text-xs">{p.subcategory || "—"}</TableCell>
                      <TableCell>{inr(p.price)}</TableCell>
                      <TableCell>{p.stock}</TableCell>
                      <TableCell>{p.featured ? "Yes" : "—"}</TableCell>
                      <TableCell className="text-right">
                        <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-2 text-soot transition-colors hover:text-obsidian" aria-label="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} className="p-2 text-soot transition-colors hover:text-destructive" aria-label="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-10 border border-seam p-6" data-testid="tile-covers-panel">
              <h3 className="font-serif text-xl">Subcategory Tile Covers</h3>
              <p className="mt-1 text-sm text-soot">Pick which product photo represents each subcategory tile on the shop page.</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {["fashion", "furnishing"].map((sec) =>
                  (SUBS_BY_SECTION[sec] || []).map((subName) => {
                    const prods = products.filter((p) => p.category === sec && p.subcategory === subName);
                    if (prods.length === 0) return null;
                    const key = `${sec}|${subName}`;
                    const current = tileCovers[key] || "auto";
                    const thumb = current !== "auto" ? current : prods[0]?.images?.[0];
                    return (
                      <div key={key} className="border border-seam p-3">
                        <div className="flex items-center gap-3">
                          <div className="h-14 w-10 shrink-0 overflow-hidden bg-linen">
                            {thumb && <img src={thumb} alt="" className="h-full w-full object-cover" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-bold uppercase tracking-wider">{subName}</p>
                            <Select
                              value={current}
                              onValueChange={(v) =>
                                setTileCovers((c) => {
                                  const next = { ...c };
                                  if (v === "auto") delete next[key];
                                  else next[key] = v;
                                  return next;
                                })
                              }
                            >
                              <SelectTrigger data-testid={`tile-cover-select-${subName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="mt-1.5 h-9 bg-transparent text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auto">Automatic (first product)</SelectItem>
                                {prods.map((p) =>
                                  p.images?.[0] ? (
                                    <SelectItem key={p.id} value={p.images[0]}>
                                      {p.name}
                                    </SelectItem>
                                  ) : null
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <Button data-testid="save-tile-covers-button" onClick={saveTileCovers} disabled={tileSaving} className="mt-6 rounded-full bg-obsidian text-bone hover:bg-olive">
                {tileSaving ? "Saving…" : "Save Tile Covers"}
              </Button>
            </div>
          </div>
        )}

        {tab === "invoices" && (
          <div className="mt-12" data-testid="admin-invoices">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">Invoices</h2>
              <Button data-testid="new-invoice-button" onClick={openInvoiceDialog} className="rounded-full bg-obsidian text-bone hover:bg-olive">
                <Plus className="h-4 w-4" /> New Invoice
              </Button>
            </div>
            {invoices.length === 0 ? (
              <p className="mt-10 font-serif text-xl italic text-soot" data-testid="invoices-empty">
                No invoices yet — create one after confirming an order on WhatsApp.
              </p>
            ) : (
              <div className="mt-6 overflow-x-auto border border-seam">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                        <TableCell className="font-mono text-xs font-bold">{inv.invoice_no}</TableCell>
                        <TableCell>{inv.customer.name}</TableCell>
                        <TableCell>{inv.items.reduce((s, i) => s + i.qty, 0)}</TableCell>
                        <TableCell>{inr(inv.total)}</TableCell>
                        <TableCell className="text-sm">{new Date(inv.created_at).toLocaleDateString("en-IN")}</TableCell>
                        <TableCell className="text-right">
                          <button data-testid={`download-invoice-${inv.id}`} onClick={() => downloadPdf(inv)} className="p-2 text-soot transition-colors hover:text-obsidian" aria-label="Download PDF">
                            <FileDown className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`share-invoice-${inv.id}`}
                            onClick={() => shareOnWhatsApp(inv)}
                            className="p-2 text-[#1FA855] transition-colors hover:text-[#178a44]"
                            aria-label="Share on WhatsApp"
                            title="Send invoice on WhatsApp"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </button>
                          <button data-testid={`delete-invoice-${inv.id}`} onClick={() => removeInvoice(inv)} className="p-2 text-soot transition-colors hover:text-destructive" aria-label="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-12" data-testid="admin-orders">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">Order Log</h2>
              <Button data-testid="log-order-button" onClick={openOrderDialog} className="rounded-full bg-obsidian text-bone hover:bg-olive">
                <Plus className="h-4 w-4" /> Log WhatsApp Order
              </Button>
            </div>
            {orderLogs.length === 0 ? (
              <p className="mt-10 font-serif text-xl italic text-soot" data-testid="orders-empty">
                No orders logged yet — record WhatsApp orders here so every sale is on the books.
              </p>
            ) : (
              <div className="mt-6 overflow-x-auto border border-seam">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderLogs.map((o) => (
                      <TableRow key={o.id} data-testid={`admin-order-row-${o.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <p className="font-serif text-base">{o.customer_name}</p>
                            {o.source === "website" && (
                              <span className="bg-olive px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bone">Website</span>
                            )}
                          </div>
                          {o.phone && <p className="text-xs text-soot">{o.phone}</p>}
                          {o.shipping?.address && (
                            <p className="max-w-52 truncate text-xs text-soot">
                              {o.shipping.address}, {o.shipping.city} {o.shipping.pincode}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>{o.items.reduce((s, i) => s + i.qty, 0)}</TableCell>
                        <TableCell>{inr(o.total)}</TableCell>
                        <TableCell className="text-sm">{new Date(o.created_at).toLocaleDateString("en-IN")}</TableCell>
                        <TableCell>
                          <Select value={o.status} onValueChange={(v) => setOrderStatus(o, v)}>
                            <SelectTrigger data-testid={`order-status-${o.id}`} className="h-8 w-32 bg-transparent text-xs capitalize">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ORDER_LOG_STATUSES.map((s) => (
                                <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <button
                            data-testid={`invoice-from-order-${o.id}`}
                            onClick={() => {
                              setTab("invoices");
                              openInvoiceDialog(o);
                            }}
                            className="p-2 text-soot transition-colors hover:text-obsidian"
                            aria-label="Create invoice from order"
                            title="Create invoice from order"
                          >
                            <FilePlus className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`delete-order-${o.id}`}
                            onClick={() => removeOrder(o)}
                            className="p-2 text-soot transition-colors hover:text-destructive"
                            aria-label="Delete order"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {tab === "about" && (
          <div className="mt-12" data-testid="admin-about">
            <h2 className="font-serif text-2xl">About Us Page Content</h2>
            <p className="mt-2 max-w-lg text-sm text-soot">
              Edit the text and images shown on the About Us page. Press Save on each section to publish your changes.
            </p>
            {!aboutContent ? (
              <p className="mt-10 text-soot">Loading content…</p>
            ) : (
              <div className="mt-8 grid gap-6 lg:grid-cols-2">
                {[
                  { key: "about_story", label: "Brand Story Section", id: "story" },
                  { key: "about_owner", label: "Owner Section", id: "owner" },
                  { key: "about_mumkey", label: "Mumkey Section", id: "mumkey" },
                ].map((block) => (
                  <div key={block.key} className="border border-seam p-6" data-testid={`about-block-${block.id}`}>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-soot">{block.label}</p>
                    <div className="mt-4 flex gap-4">
                      <div className="h-32 w-24 shrink-0 overflow-hidden bg-linen">
                        {aboutContent[block.key]?.image && (
                          <img src={aboutContent[block.key].image} alt="" className="h-full w-full object-cover" data-testid={`about-${block.id}-image-preview`} />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <Input
                          data-testid={`about-${block.id}-image-input`}
                          placeholder="Image URL"
                          value={aboutContent[block.key]?.image || ""}
                          onChange={(e) => setAboutContent((c) => ({ ...c, [block.key]: { ...c[block.key], image: e.target.value } }))}
                          className="bg-transparent text-xs"
                        />
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          id={`about-file-${block.id}`}
                          className="hidden"
                          onChange={(e) => uploadContentImage(block.key, e)}
                          data-testid={`about-${block.id}-file-input`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid={`about-${block.id}-upload-button`}
                          onClick={() => document.getElementById(`about-file-${block.id}`)?.click()}
                        >
                          <ImagePlus className="h-4 w-4" /> Upload Image
                        </Button>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label>Title</Label>
                      <Input
                        data-testid={`about-${block.id}-title-input`}
                        value={aboutContent[block.key]?.title || ""}
                        onChange={(e) => setAboutContent((c) => ({ ...c, [block.key]: { ...c[block.key], title: e.target.value } }))}
                        className="mt-1.5 bg-transparent"
                      />
                    </div>
                    <div className="mt-4">
                      <Label>Text (blank line = new paragraph)</Label>
                      <Textarea
                        data-testid={`about-${block.id}-text-input`}
                        rows={7}
                        value={aboutContent[block.key]?.text || ""}
                        onChange={(e) => setAboutContent((c) => ({ ...c, [block.key]: { ...c[block.key], text: e.target.value } }))}
                        className="mt-1.5 bg-transparent"
                      />
                    </div>
                    <Button
                      data-testid={`about-${block.id}-save-button`}
                      disabled={contentSaving === block.key}
                      onClick={() => saveContent(block.key)}
                      className="mt-4 h-10 w-full rounded-full bg-obsidian text-bone hover:bg-olive"
                    >
                      {contentSaving === block.key ? "Saving…" : "Save Section"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "reviews" && (
          <div className="mt-12" data-testid="admin-reviews">
            <h2 className="font-serif text-2xl">Customer Reviews</h2>
            {reviews.length === 0 ? (
              <p className="mt-10 font-serif text-xl italic text-soot" data-testid="admin-reviews-empty">
                No reviews yet — they will appear here as customers post them.
              </p>
            ) : (
              <div className="mt-6 overflow-x-auto border border-seam">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Reviewer</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Review</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.map((r) => (
                      <TableRow key={r.id} data-testid={`admin-review-row-${r.id}`}>
                        <TableCell className="font-serif text-base">{r.product_name}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>
                          <span className="text-obsidian">{"★".repeat(r.rating)}</span>
                          <span className="text-seam">{"★".repeat(5 - r.rating)}</span>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          {r.image && <img src={r.image} alt="" className="mb-1 h-10 w-8 border border-seam object-cover" />}
                          <p className="truncate">{r.text}</p>
                        </TableCell>
                        <TableCell className="text-sm">{new Date(r.created_at).toLocaleDateString("en-IN")}</TableCell>
                        <TableCell className="text-right">
                          <button
                            data-testid={`delete-review-${r.id}`}
                            onClick={() => removeReview(r)}
                            className="p-2 text-soot transition-colors hover:text-destructive"
                            aria-label="Delete review"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PRODUCT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-bone sm:max-w-lg" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">{editing ? "Edit Product" : "New Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="mt-2 space-y-4">
            <div>
              <Label htmlFor="p-name">Name</Label>
              <Input id="p-name" data-testid="product-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 bg-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="p-price">Price (₹)</Label>
                <Input id="p-price" data-testid="product-price-input" type="number" min="1" step="0.01" required value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
              <div>
                <Label htmlFor="p-stock">Stock</Label>
                <Input id="p-stock" data-testid="product-stock-input" type="number" min="0" required value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Section</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v, subcategory: SUBS_BY_SECTION[v][0] }))}>
                  <SelectTrigger data-testid="product-section-select" className="mt-1.5 bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fashion">Fashion Wear</SelectItem>
                    <SelectItem value="furnishing">Home Furnishing</SelectItem>
                    <SelectItem value="mumkey">Mumkey (Maternity)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.subcategory} onValueChange={(v) => setForm((f) => ({ ...f, subcategory: v }))}>
                  <SelectTrigger data-testid="product-subcategory-select" className="mt-1.5 bg-transparent">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(SUBS_BY_SECTION[form.category] || []).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="p-desc">Description</Label>
              <Textarea id="p-desc" data-testid="product-description-input" rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1.5 bg-transparent" />
            </div>

            <div>
              <Label>Images</Label>
              {form.images.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="product-images-preview">
                  {form.images.map((url, i) => (
                    <div key={i} className="group relative h-20 w-16 overflow-hidden border border-seam bg-linen">
                      <img src={url} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        data-testid={`remove-image-${i}`}
                        onClick={() => removeImage(i)}
                        aria-label="Remove image"
                        className="absolute right-0.5 top-0.5 bg-obsidian/80 p-0.5 text-bone opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      {i === 0 && <span className="absolute bottom-0 left-0 bg-bone/90 px-1 text-[8px] font-bold uppercase">Main</span>}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadFile} className="hidden" data-testid="product-image-file-input" />
                <Button
                  type="button"
                  data-testid="upload-image-button"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1"
                >
                  <ImagePlus className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload Image"}
                </Button>
              </div>
              <div className="mt-2 flex gap-2">
                <Input
                  data-testid="product-image-url-input"
                  placeholder="…or paste an image URL"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="bg-transparent"
                />
                <Button type="button" data-testid="add-image-url-button" variant="outline" onClick={addImageUrl}>
                  Add
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="p-sizes">Sizes (comma separated, leave empty for home pieces)</Label>
              <Input id="p-sizes" data-testid="product-sizes-input" placeholder="XS,S,M,L" value={form.sizesText} onChange={(e) => setForm((f) => ({ ...f, sizesText: e.target.value }))} className="mt-1.5 bg-transparent" />
            </div>
            <div className="flex items-center justify-between border border-seam px-4 py-3">
              <Label htmlFor="p-featured">Feature on home page</Label>
              <Switch id="p-featured" data-testid="product-featured-switch" checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} />
            </div>
            <div className="flex items-center justify-between border border-seam px-4 py-3">
              <Label htmlFor="p-is-new">Tag as New arrival (shows a NEW badge)</Label>
              <Switch id="p-is-new" data-testid="product-is-new-switch" checked={form.is_new} onCheckedChange={(v) => setForm((f) => ({ ...f, is_new: v }))} />
            </div>
            <Button type="submit" data-testid="product-save-button" disabled={saving} className="h-11 w-full rounded-full bg-obsidian text-bone hover:bg-olive">
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Product"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* INVOICE DIALOG */}
      <Dialog open={invDialogOpen} onOpenChange={setInvDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-bone sm:max-w-xl" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">New Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveInvoice} className="mt-2 space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="inv-name">Customer Name</Label>
                <Input id="inv-name" data-testid="invoice-customer-name-input" required value={invForm.name} onChange={(e) => setInvForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
              <div>
                <Label htmlFor="inv-phone">Phone</Label>
                <Input id="inv-phone" data-testid="invoice-customer-phone-input" value={invForm.phone} onChange={(e) => setInvForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="inv-address">Address</Label>
                <Textarea id="inv-address" data-testid="invoice-customer-address-input" rows={2} value={invForm.address} onChange={(e) => setInvForm((f) => ({ ...f, address: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Items</Label>
                <Button type="button" data-testid="invoice-add-item-button" variant="outline" size="sm" onClick={() => setInvItems((r) => [...r, { ...emptyInvItem }])}>
                  <Plus className="h-3.5 w-3.5" /> Add Item
                </Button>
              </div>
              <div className="mt-3 space-y-3">
                {invItems.map((row, idx) => (
                  <div key={idx} className="border border-seam p-3" data-testid={`invoice-item-${idx}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <Select value={row.product_id} onValueChange={(v) => pickProduct(idx, v)}>
                          <SelectTrigger data-testid={`invoice-item-product-${idx}`} className="bg-transparent text-xs">
                            <SelectValue placeholder="Pick a product…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Custom item…</SelectItem>
                            {products.map((p) => (
                              <SelectItem key={p.id} value={p.id} className="text-xs">
                                {p.name} — {inr(p.price)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {invItems.length > 1 && (
                        <button type="button" data-testid={`invoice-item-remove-${idx}`} onClick={() => setInvItems((r) => r.filter((_, i) => i !== idx))} aria-label="Remove item" className="p-2 text-soot hover:text-destructive">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-12 gap-2">
                      <Input data-testid={`invoice-item-name-${idx}`} placeholder="Item name" value={row.name} onChange={(e) => updateInvItem(idx, "name", e.target.value)} className="col-span-7 bg-transparent text-sm" />
                      <Input data-testid={`invoice-item-price-${idx}`} type="number" min="0" step="0.01" placeholder="Price" value={row.price} onChange={(e) => updateInvItem(idx, "price", e.target.value)} className="col-span-3 bg-transparent text-sm" />
                      <Input data-testid={`invoice-item-qty-${idx}`} type="number" min="1" placeholder="Qty" value={row.qty} onChange={(e) => updateInvItem(idx, "qty", e.target.value)} className="col-span-2 bg-transparent text-sm" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="inv-tax">Tax % (GST, optional)</Label>
                <Input id="inv-tax" data-testid="invoice-tax-input" type="number" min="0" max="100" step="0.01" value={invForm.tax} onChange={(e) => setInvForm((f) => ({ ...f, tax: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
              <div>
                <Label htmlFor="inv-notes">Notes (optional)</Label>
                <Input id="inv-notes" data-testid="invoice-notes-input" value={invForm.notes} onChange={(e) => setInvForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-seam pt-4">
              <p className="text-sm text-soot">
                Subtotal {inr(invSubtotal)} · Tax {inr(invTax)}
              </p>
              <p className="font-serif text-xl" data-testid="invoice-total-preview">Total {inr(invSubtotal + invTax)}</p>
            </div>
            <Button type="submit" data-testid="invoice-save-button" disabled={invSaving} className="h-11 w-full rounded-full bg-obsidian text-bone hover:bg-olive">
              {invSaving ? "Creating…" : "Create Invoice"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ORDER DIALOG */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-bone sm:max-w-xl" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Log WhatsApp Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveOrder} className="mt-2 space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="ord-name">Customer Name</Label>
                <Input id="ord-name" data-testid="order-customer-name-input" required value={orderForm.name} onChange={(e) => setOrderForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
              <div>
                <Label htmlFor="ord-phone">Phone</Label>
                <Input id="ord-phone" data-testid="order-customer-phone-input" value={orderForm.phone} onChange={(e) => setOrderForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1.5 bg-transparent" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Items</Label>
                <Button type="button" data-testid="order-add-item-button" variant="outline" size="sm" onClick={() => setOrderItems((r) => [...r, { ...emptyInvItem }])}>
                  <Plus className="h-3.5 w-3.5" /> Add Item
                </Button>
              </div>
              <div className="mt-3 space-y-3">
                {orderItems.map((row, idx) => (
                  <div key={idx} className="border border-seam p-3" data-testid={`order-item-${idx}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <Select value={row.product_id} onValueChange={(v) => pickOrderProduct(idx, v)}>
                          <SelectTrigger data-testid={`order-item-product-${idx}`} className="bg-transparent text-xs">
                            <SelectValue placeholder="Pick a product…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Custom item…</SelectItem>
                            {products.map((p) => (
                              <SelectItem key={p.id} value={p.id} className="text-xs">
                                {p.name} — {inr(p.price)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {orderItems.length > 1 && (
                        <button type="button" data-testid={`order-item-remove-${idx}`} onClick={() => setOrderItems((r) => r.filter((_, i) => i !== idx))} aria-label="Remove item" className="p-2 text-soot hover:text-destructive">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-12 gap-2">
                      <Input data-testid={`order-item-name-${idx}`} placeholder="Item name" value={row.name} onChange={(e) => updateOrderItem(idx, "name", e.target.value)} className="col-span-7 bg-transparent text-sm" />
                      <Input data-testid={`order-item-price-${idx}`} type="number" min="0" step="0.01" placeholder="Price" value={row.price} onChange={(e) => updateOrderItem(idx, "price", e.target.value)} className="col-span-3 bg-transparent text-sm" />
                      <Input data-testid={`order-item-qty-${idx}`} type="number" min="1" placeholder="Qty" value={row.qty} onChange={(e) => updateOrderItem(idx, "qty", e.target.value)} className="col-span-2 bg-transparent text-sm" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="ord-notes">Notes (optional)</Label>
              <Input id="ord-notes" data-testid="order-notes-input" value={orderForm.notes} onChange={(e) => setOrderForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1.5 bg-transparent" />
            </div>

            <div className="flex items-center justify-between border-t border-seam pt-4">
              <p className="text-sm text-soot">{orderItems.filter((r) => r.name.trim()).length} item(s)</p>
              <p className="font-serif text-xl" data-testid="order-total-preview">Total {inr(orderTotal)}</p>
            </div>
            <Button type="submit" data-testid="order-save-button" disabled={orderSaving} className="h-11 w-full rounded-full bg-obsidian text-bone hover:bg-olive">
              {orderSaving ? "Logging…" : "Log Order"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
